import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubmissions } from '@/hooks/useSubmissions';

export interface NotificationItem {
  id: string;
  title: string;
  description: string;
  date: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const { epiRequests, incidents, denunciations } = useSubmissions();
  const storageKey = `portal-notifications-seen-${user?.id || 'guest'}`;
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(storageKey) || '');

  const notifications = useMemo<NotificationItem[]>(() => {
    if (!user) return [];

    if (user.perfil === 'admin') {
      return [
        ...epiRequests.map((item) => ({
          id: `epi-${item.id}`,
          title: 'Nova solicitação de EPI',
          description: `${item.displayId || item.id} - ${item.userName}`,
          date: item.createdAt,
        })),
        ...incidents.map((item) => ({
          id: `inc-${item.id}`,
          title: 'Novo registro de incidente',
          description: `${item.displayId || item.id} - ${item.userName}`,
          date: item.createdAt,
        })),
        ...denunciations.map((item) => ({
          id: `den-${item.id}`,
          title: 'Nova denúncia recebida',
          description: `${item.displayId || item.id} - ${item.identificar ?item.nomeIdentificado || 'Identificada' : 'Anônima'}`,
          date: item.createdAt,
        })),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

    return [
      ...epiRequests
        .filter((item) => item.userId === user.id && item.status !== 'Pendente')
        .map((item) => ({
          id: `epi-status-${item.id}`,
          title: 'Status da solicitação atualizado',
          description: `${item.displayId || item.id} - ${item.status}`,
          date: item.createdAt,
        })),
      ...incidents
        .filter((item) => item.userId === user.id && item.status !== 'Em análise')
        .map((item) => ({
          id: `inc-status-${item.id}`,
          title: 'Status do incidente atualizado',
          description: `${item.displayId || item.id} - ${item.status}`,
          date: item.createdAt,
        })),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [denunciations, epiRequests, incidents, user]);

  const unreadCount = notifications.filter((item) => !lastSeen || item.date > lastSeen).length;

  const markAllAsSeen = () => {
    const value = new Date().toISOString();
    localStorage.setItem(storageKey, value);
    setLastSeen(value);
  };

  return { notifications: notifications.slice(0, 8), unreadCount, markAllAsSeen };
}
